import type { UseCommonData_CommonDataQuery } from "@hooks/useCommonData/__generated__/queries";
import type { TypeSearchQueryParams } from "@hooks/useSearchQueryParams";
import type { DynamicUrlParams_MatchFragment } from "@libs/utils/dynamicURLParams/__generated__/fragments";
import {
  InvalidParametersError,
  NotFoundError,
  reduceStaticParameters,
  translateTeamParameters,
} from "./common";
import type { ParametersForStaticUrl } from "./common";

export type ParametersForShopsStaticUrl = Pick<
  ParametersForStaticUrl,
  | "prefecture"
  | "combinedArea"
  | "area"
  | "genre"
  | "tournament"
  | "team"
  | "match"
>;

export type AreaParameters = Pick<
  ParametersForShopsStaticUrl,
  "prefecture" | "combinedArea" | "area"
>;

export type TeamParameters = Pick<
  ParametersForShopsStaticUrl,
  "genre" | "tournament" | "team" | "match"
>;

type MappedCombinedArea = {
  combinedArea: string;
  area: string;
};

const mapCombinedAreas = (
  combinedAreas: UseCommonData_CommonDataQuery["masterPrefectures"][number]["combinedAreas"],
  params: TypeSearchQueryParams
): MappedCombinedArea[] => {
  if (!combinedAreas || combinedAreas.length === 0) {
    return [];
  }

  return combinedAreas.map((combinedArea) => {
    const selectedSingleAreas: { area: string }[] = [];
    const singleAreas = combinedArea.areas ? combinedArea.areas : [];

    singleAreas.forEach((sa) => {
      const areaSlug = sa.slug ? sa.slug : "";
      if (params.areas?.includes(sa.id)) {
        selectedSingleAreas.push({ area: areaSlug });
      }
    });

    const slug = combinedArea.slug ?? "";

    if (selectedSingleAreas.length === 1) {
      return { combinedArea: slug, ...selectedSingleAreas[0] };
    } else if (singleAreas.length === selectedSingleAreas.length) {
      return { combinedArea: slug, area: "" };
    } else if (selectedSingleAreas.length === 0) {
      return { combinedArea: "", area: "" };
    } else {
      throw new InvalidParametersError(
        "combined area has more than one but less than all of its areas selected"
      );
    }
  });
};

const mapPrefecture = (
  prefecture: UseCommonData_CommonDataQuery["masterPrefectures"][number],
  params: TypeSearchQueryParams
): AreaParameters => {
  const combinedAreas = prefecture.combinedAreas
    ? prefecture.combinedAreas
    : [];
  const mappedCombinedAreas: MappedCombinedArea[] = mapCombinedAreas(
    combinedAreas,
    params
  );

  // combinedArea, areaの意味は以下の通り
  // - combinedArea: 結合エリア
  // - area: 単体エリア
  const selectedCombinedAreas: MappedCombinedArea[] = [];
  const selectedAreasByCombinedArea: MappedCombinedArea[] = [];
  const selectedNoAreaByCombinedArea: MappedCombinedArea[] = [];

  for (let i = 0; i < combinedAreas.length; i++) {
    const combinedArea = combinedAreas[i];
    const singleAreas = combinedArea.areas ?? [];
    const mappedCombinedArea = mappedCombinedAreas[i];
    const combinedAreaSlug = combinedArea.slug ?? "";

    if (params.combinedAreas?.includes(combinedArea.id)) {
      selectedCombinedAreas.push({
        combinedArea: combinedAreaSlug,
        // 結合エリア内の単体エリアが1つしか無い場合、その1つを選択すると結合エリア選択が有効な状態になってしまう（全選択の状態と同じ意味になる）
        // 結合エリア選択が有効な状態な状態でも、選択している単体エリアが1つだけの場合、明示的にエリアの値をセットする（セットしたエリアの値はcanonicalで使用する）
        area: singleAreas.length === 1 ? mappedCombinedArea.area : "",
      });
    } else if (
      mappedCombinedArea.combinedArea !== "" &&
      mappedCombinedArea.area !== ""
    ) {
      // 以下の条件の時に実行される処理
      // - 結合アリア内に複数の単体エリアがある
      // - 単体エリアを1つだけ選択している
      selectedAreasByCombinedArea.push(mappedCombinedArea);
    } else if (
      mappedCombinedArea.combinedArea === "" &&
      mappedCombinedArea.area === ""
    ) {
      // 以下の条件の時に実行される処理
      // - 結合アリア内の単体エリアを1つも選択していない
      selectedNoAreaByCombinedArea.push(mappedCombinedArea);
    }
  }

  const numberOfCombinedAreas = combinedAreas.length;
  const prefectureSlug = prefecture.slug ?? "";

  if (
    numberOfCombinedAreas > 1 &&
    numberOfCombinedAreas === selectedCombinedAreas.length
  ) {
    // 結合エリアが2つ以上あり、全ての結合エリアが選択されている場合
    return { prefecture: prefectureSlug, combinedArea: "", area: "" };
  } else if (
    selectedCombinedAreas.length === 1 &&
    selectedNoAreaByCombinedArea.length === numberOfCombinedAreas - 1
  ) {
    // 以下の条件の時に実行される処理
    // - 結合エリアを1つ選択している
    // - 選択している結合エリア以外、単体エリアも含め1つも選択していない
    return { prefecture: prefectureSlug, ...selectedCombinedAreas[0] };
  } else if (
    selectedAreasByCombinedArea.length === 1 &&
    selectedNoAreaByCombinedArea.length === numberOfCombinedAreas - 1
  ) {
    // 以下の条件の時に実行される処理
    // - 結合エリア内の単体エリアを1つ選択している
    // - 選択している1つの単体エリア以外の「結合エリア」「単体エリア」を1つも選択していない
    return {
      prefecture: prefectureSlug,
      ...selectedAreasByCombinedArea[0],
    };
  } else if (numberOfCombinedAreas === selectedNoAreaByCombinedArea.length) {
    // 以下の条件の時に実行される処理
    // - 結合エリア、結合エリア内の単体エリアを1つも選択していない
    return { prefecture: "", combinedArea: "", area: "" };
  } else {
    // unselected child with one selected child > 1
    // selected children > 1 & < children length
    throw new InvalidParametersError(
      "prefecture has selection with no matching static url parameters"
    );
  }
};

export const translateAreaParameters = (
  prefectures: UseCommonData_CommonDataQuery["masterPrefectures"],
  params: TypeSearchQueryParams
): AreaParameters => {
  const mappedPrefectures: AreaParameters[] = prefectures.map((p) =>
    mapPrefecture(p, params)
  );

  const prefecturesWithStaticURLParameters: AreaParameters[] = [];
  mappedPrefectures.forEach((mappedPrefecture) => {
    if (mappedPrefecture.prefecture !== "") {
      prefecturesWithStaticURLParameters.push(mappedPrefecture);
    }
  });

  if (prefecturesWithStaticURLParameters.length === 1) {
    return prefecturesWithStaticURLParameters[0];
  } else if (prefecturesWithStaticURLParameters.length === 0) {
    return { prefecture: "", combinedArea: "", area: "" };
  } else {
    throw new InvalidParametersError(
      "more than one prefecture has selected children or grandchildren"
    );
  }
};

export const translateTeamParametersWithMatch = (
  commonData: Pick<
    UseCommonData_CommonDataQuery,
    "teamsUnbundled" | "tournaments"
  >,
  matchData: DynamicUrlParams_MatchFragment | null | undefined,
  params: TypeSearchQueryParams
): TeamParameters => {
  const { match } = params;

  if (match) {
    if (
      !matchData ||
      !matchData.masterTournament ||
      !matchData.masterTournament.slug ||
      !matchData.homeTeam ||
      !matchData.homeTeam.masterSportGenre.slug
    ) {
      throw new NotFoundError("could not find static parameters in match data");
    }

    const {
      masterTournament: { slug: tournamentSlug },
      homeTeam: {
        masterSportGenre: { slug: genreSlug },
      },
    } = matchData;

    return {
      genre: genreSlug,
      tournament: tournamentSlug,
      team: "",
      match: String(match),
    };
  }

  const teamParameters = translateTeamParameters(commonData, params);
  return { ...teamParameters, match: "" };
};

const translateParameters = (
  commonData: UseCommonData_CommonDataQuery,
  matchData: DynamicUrlParams_MatchFragment | null | undefined,
  params: TypeSearchQueryParams
): ParametersForShopsStaticUrl => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { masterPrefectures, __typename, ...rest } = commonData;
  return {
    ...translateAreaParameters(masterPrefectures, params),
    ...translateTeamParametersWithMatch(rest, matchData, params),
  };
};

export const getStaticRepresentation = (
  commonData: UseCommonData_CommonDataQuery,
  matchData: DynamicUrlParams_MatchFragment | null | undefined,
  params: TypeSearchQueryParams
): string | null => {
  const parametersForShopsStaticUrl = translateParameters(
    commonData,
    matchData,
    params
  );
  // No error was thrown but need to check if any static parameters were returned
  // All static URLs start with a prefecture or genre parameter
  const hasStaticRepresentation =
    parametersForShopsStaticUrl.prefecture || parametersForShopsStaticUrl.genre;
  if (hasStaticRepresentation) {
    return reduceStaticParameters(parametersForShopsStaticUrl, "/shops");
  }
  return null;
};
